import { useNavigate } from "react-router-dom";
import { getCurrentLanguageFromUrl } from "../utils/languageUtils";

/**
 * Custom hook to navigate while preserving the current language in the URL.
 */
const useNavigateWithLang = () => {
  const navigate = useNavigate();
  const lang = getCurrentLanguageFromUrl();

  /**
   * Navigate to a given path while appending the current language.
   * @param path The path to navigate to (without language prefix).
   */
  const navigateWithLang = (path: string) => {
    navigate(`/${lang}${path}`);
  };

  /**
   * Navigate to a page by its name.
   * @param name The name of the page to navigate to.
   * @param params Optional parameters for dynamic routes.
   */
  const navigateByName = (name: string, params?: Record<string, string>) => {
    const routes: Record<string, string> = {
      home: "/",
      movies: "/movies",
      comics: "/comics",
      games: "/games",
      list: "/list",
      movieDetails: `/movie/${params?.slug || ""}/`,
      category: `/movie/category/${params?.slug || ""}/`,
      comicDetails: `/comic/${params?.slug || ""}/`,
      comicCategory: `/comic/category/${params?.slug || ""}/`,
      novelDetails: `/novel/${params?.slug || ""}/`,
      gameDetails: `/game/${params?.slug || ""}/`,
      gameCategory: `/game/category/${params?.slug || ""}/`,
      comicReader: `/comic/${params?.slug || ""}/${params?.chapterIndex || ""}/`,
      novelReader: `/novel/${params?.slug || ""}/${params?.chapterIndex || ""}/`,
      login: "/user/login/",
      register: "/user/register/",
      search: "/search/",
      history: "/history",
      saved: "/saved",
      notFound: "/*",
    };

    const path = routes[name];
    if (path) {
      navigateWithLang(path);
    } else {
      console.error(`Route with name '${name}' not found.`);
    }
  };

  return {
    navigateToHome: () => navigateWithLang("/"),
    navigateToMovies: () => navigateWithLang("/movies"),
    navigateToComics: () => navigateWithLang("/comics"),
    navigateToGames: () => navigateWithLang("/games"),
    navigateToList: () => navigateWithLang("/list"),
    navigateToMovieDetails: (slug: string) => navigateWithLang(`/movie/${slug}/`),
    navigateToCategory: (slug: string) => navigateWithLang(`/movie/category/${slug}/`),
    navigateToComicDetails: (slug: string) => navigateWithLang(`/comic/${slug}/`),
    navigateToComicCategory: (slug: string) => navigateWithLang(`/comic/category/${slug}/`),
    navigateToNovelDetails: (slug: string) => navigateWithLang(`/novel/${slug}/`),
    navigateToGameDetails: (slug: string) => navigateWithLang(`/game/${slug}/`),
    navigateToGameCategory: (slug: string) => navigateWithLang(`/game/category/${slug}/`),
    navigateToComicReader: (slug: string, chapterIndex: string) => navigateWithLang(`/comic/${slug}/${chapterIndex}/`),
    navigateToNovelReader: (slug: string, chapterIndex: string) => navigateWithLang(`/novel/${slug}/${chapterIndex}/`),
    navigateToLogin: () => navigateWithLang("/user/login/"),
    navigateToRegister: () => navigateWithLang("/user/register/"),
    navigateToSearch: () => navigateWithLang("/search/"),
    navigateToHistory: () => navigateWithLang("/history"),
    navigateToSaved: () => navigateWithLang("/saved"),
    navigateToNotFound: () => navigateWithLang("/*"),
    navigateByName,
  };
};

export default useNavigateWithLang;
